import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

export default function BubbleChart({ category, periodCount, width = 600, height = 400 }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!category?.values || !periodCount) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();

    // Prepare data
    const data = category.values.map((value) => ({
      name: value.title,
      value: periodCount[value.id] || 0,
    }));

    // If all values are 0, return a blank chart with a message and remove initial content
    if (data.every((d) => d.value === 0)) {
      // Remove initial content
      d3.select(svgRef.current).selectAll('*').remove();

      // Create SVG
      const svg = d3
        .select(svgRef.current)
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', [0, 0, width, height])
        .attr('style', 'max-width: 100%; height: auto;');

      // Add message
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '1.5em')
        .style('fill', 'white')
        .text('No data available.');

      return;
    }

    // Create bubble layout
    const bubble = d3.pack().size([width, height]).padding(2);

    // Create hierarchy
    const root = d3.hierarchy({ children: data }).sum((d) => d.value);

    // Create color scale
    const color = d3.scaleOrdinal(d3.schemeCategory10);

    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto;');

    // Create nodes
    const nodes = svg
      .selectAll('g')
      .data(bubble(root).descendants().slice(1))
      .join('g')
      .attr('transform', (d) => `translate(${d.x},${d.y})`);

    // Add circles
    nodes
      .append('circle')
      .attr('r', (d) => d.r)
      .attr('fill', (d, i) => color(i))
      .attr('opacity', 0.7)
      .attr('class', 'lift')
      .on('mouseover', function () {
        d3.select(this).attr('opacity', 1).attr('stroke', '#fff').attr('stroke-width', 2);
      })
      .on('mouseout', function () {
        d3.select(this).attr('opacity', 0.7).attr('stroke', 'none');
      });

    // Add labels
    nodes
      .append('text')
      .attr('dy', '.3em')
      .style('text-anchor', 'middle')
      .style('font-size', (d) => `${d.r / 5}px`)
      .style('fill', 'white')
      .text((d) => d.data.name)
      .each(function (d) {
        const self = d3.select(this);
        const text = self.text();
        if (this.getComputedTextLength() > d.r * 2) {
          self.text(`${text.slice(0, 10)}...`);
        }
      });

    // Add value labels
    nodes
      .append('text')
      .attr('dy', '1.3em')
      .style('text-anchor', 'middle')
      .style('font-size', (d) => `${d.r / 5}px`)
      .style('fill', 'white')
      .text((d) => d.data.value);
  }, [category, periodCount, width, height]);

  return <svg ref={svgRef} />;
}
